import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
};

/**
 * Button system. Primary uses the Pryma signature violet→pink gradient — the only
 * place the spectrum is allowed as a fill. Used across all pages identically.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className = '', children, ...rest }, ref) => {
    const sizes = {
      sm: 'h-8 px-3 text-[13px]',
      md: 'h-10 px-4 text-[14px]',
      lg: 'h-12 px-5 text-[15px]',
    };
    const variants = {
      primary:
        'bg-pryma-grad text-white font-semibold hover:opacity-95 active:opacity-90 ring-violet-focus',
      secondary:
        'bg-surface text-ink border border-borderStrong hover:bg-slate50 ring-violet-focus',
      ghost: 'bg-transparent text-body hover:text-ink hover:bg-slate100 ring-violet-focus',
      dark: 'bg-ink text-white font-semibold hover:bg-ink/90 ring-violet-focus',
    };
    return (
      <button
        ref={ref}
        className={`inline-flex items-center justify-center gap-1.5 rounded-lg transition-all duration-150 ${sizes[size]} ${variants[variant]} ${className}`}
        {...rest}
      >
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';
export default Button;
